import {Component, forwardRef, inject, Injector, Input, ViewChild} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormControlDirective,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from "@angular/forms";
import {isArray} from "ngx-bootstrap/chronos";
import {FormGroupComponent} from '../../form-group/form-group.component';
import {LocaleService} from '../../../services/locale-service/locale.service';
import {DecimalPipe, getLocaleNumberSymbol, NumberSymbol} from '@angular/common';

@Component({
  selector: 'app-money-filter',
  templateUrl: './money-filter.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MoneyFilterComponent),
      multi: true,
    }
  ],
  imports: [FormGroupComponent, ReactiveFormsModule],
  standalone: true
})
export class MoneyFilterComponent implements ControlValueAccessor {
  private readonly injector = inject(Injector);
  private localeService = inject(LocaleService);
  private decimalPipe = inject(DecimalPipe);

  @Input() public placeholder = ''
  @Input() public label!: string
  @Input() public inline = true;
  @Input() public small = true;
  @Input() public name?: string;
  @ViewChild(FormControlDirective, {static: true})
  public formControlDirective?: FormControlDirective;
  @Input()
  public formControl?: FormControl<string[] | string | null>
  @Input()
  public formControlName!: string;
  private fb = inject(FormBuilder);
  public formGroup = this.fb.group({
    min: this.fb.control<string | ''>('', {updateOn: 'blur'}),
    max: this.fb.control<string | ''>('', {updateOn: 'blur'}),
  })
  private locale: "de" | "en";
  private lastUsedLocale?: "de" | "en";

  constructor() {

    this.localeService.localeChanged.subscribe((l) => {
      this.locale = l;
      this.reformatValues();
    });
    this.locale = this.localeService.currentLocale;

    this.formGroup.valueChanges.subscribe(
      v => {
        if (this.formGroup.valid) {
          if (!v.min && !v.max) {
            this.control.setValue('');
          } else {

            const min = this.reverseFormat(v.min);
            const max = this.reverseFormat(v.max);
            this.control.setValue(min + ',' + max);
          }
        }
      }
    )
  }

  public get control(): FormControl<string[] | string | null> {
    if (this.formControl) {
      return this.formControl;
    }

    if (this.controlContainer && this.controlContainer.control) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const control = this.controlContainer.control.get(this.formControlName);
      if (control) {
        return <FormControl<string[] | string | null>>control;
      }
    }

    // Return a new FormControl as a fallback to avoid runtime errors.
    return new FormControl();
  }

  get controlContainer(): ControlContainer {
    return this.injector.get(ControlContainer);
  }

  registerOnTouched(fn: () => void): void {
    this.formControlDirective?.valueAccessor?.registerOnTouched(fn);
  }

  registerOnChange(fn: () => void): void {
    this.formControlDirective?.valueAccessor?.registerOnChange(fn);
  }

  writeValue(value: string[] | string | null): void {

    if (value && isArray(value) && value?.length === 2) {
      this.formGroup.patchValue({
        min: this.formatNumber(value[0] as unknown as string),
        max: this.formatNumber(value[1] as unknown as string)
      }, {emitEvent: false})
    } else if (value && typeof value === 'string' && value.includes(',')) {
      const split = value.split(',')
      this.formGroup.patchValue({
        min: this.formatNumber(split[0]),
        max: this.formatNumber(split[1])
      }, {emitEvent: false})
    } else {
      this.formGroup.patchValue({
        min: '',
        max: ''
      }, {emitEvent: false})
    }
  }

  setDisabledState(): void {
    // da formControl und control identisch sind brauchen wir hier nichts weiter zu geben

  }

  private formatNumber(num: string): string {
    if (!num) return '';
    const value = parseInt(num, 10) / 100;
    this.lastUsedLocale = this.locale;  // Store the locale used for formatting
    return this.decimalPipe.transform(value, '1.2-2', this.locale) || '';
  }

  private reverseFormat(value?: string | null): string {
    if (!value) return '';

    const decimalSeparator = getLocaleNumberSymbol(this.lastUsedLocale ?? this.locale, NumberSymbol.Decimal);
    const groupSeparator = getLocaleNumberSymbol(this.lastUsedLocale ?? this.locale, NumberSymbol.Group);

    // Remove the group separator (like thousands separator) and replace the decimal separator with a dot.
    const normalizedValue = parseFloat(value.replace(new RegExp(`\\${groupSeparator}`, 'g'), '').replace(decimalSeparator, '.'));

    return (normalizedValue * 100).toString();
  }

  private reformatValues(): void {
    const min = this.formGroup.get('min')?.value;
    const max = this.formGroup.get('max')?.value;

    // Convert formatted values back to the original format
    const minOriginal = this.reverseFormat(min);
    const maxOriginal = this.reverseFormat(max);

    // Reapply the new format
    this.formGroup.patchValue({
      min: this.formatNumber(minOriginal),
      max: this.formatNumber(maxOriginal)
    }, {emitEvent: false});
  }
}
